/*
  CAUCE — comportamiento de las secciones cauce-*.
  Vanilla, sin dependencias, cuatro custom elements. Se carga con defer.

  Todo lo que se puede resolver en CSS o con HTML nativo esta resuelto ahi:
  los acordeones son <details>, los carruseles son scroll-snap. Este archivo
  solo cubre lo que el navegador no da gratis.
*/
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     <cauce-tabs> — patron tabs del APG: aria-selected, roving tabindex y
     navegacion por flechas. Sin JS los paneles quedan todos visibles, que es
     la degradacion correcta: se ve todo el contenido, solo sin pestanas.
     -------------------------------------------------------------------- */
  class CauceTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (this.tabs.length < 2) return;

      this.panels = this.tabs.map(function (tab) {
        return document.getElementById(tab.getAttribute('aria-controls'));
      });

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      // Editor de temas: al elegir un bloque que vive en un panel oculto (una
      // fila de la tabla de ingredientes), se abre su pestana.
      this.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
    }

    onBlockSelect(e) {
      for (let i = 0; i < this.panels.length; i++) {
        if (this.panels[i] && this.panels[i].contains(e.target)) this.select(i);
      }
    }

    onClick(e) {
      const tab = e.target.closest('[role="tab"]');
      if (!tab) return;
      this.select(this.tabs.indexOf(tab));
    }

    onKeydown(e) {
      if (this.tabs.indexOf(document.activeElement) === -1) return;

      const last = this.tabs.length - 1;
      const i = this.tabs.indexOf(document.activeElement);
      let next = -1;

      if (e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
      else if (e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      else return;

      e.preventDefault();
      this.select(next);
      this.tabs[next].focus();
    }

    select(index) {
      if (index < 0 || index >= this.tabs.length) return;
      for (let i = 0; i < this.tabs.length; i++) {
        const on = i === index;
        this.tabs[i].setAttribute('aria-selected', String(on));
        this.tabs[i].tabIndex = on ? 0 : -1;
        if (this.panels[i]) this.panels[i].hidden = !on;
      }
    }
  }

  /* ----------------------------------------------------------------------
     <cauce-ugc> — carrusel de videos verticales.

     El scroll y el snap los hace CSS. Aca solo:
     - play / pause por tarjeta, con un solo video sonando a la vez
     - toggle de sonido, que arranca siempre silenciado
     - pausa automatica de lo que sale de pantalla, para no dejar un video
       corriendo en background comiendose la bateria
     -------------------------------------------------------------------- */
  class CauceUgc extends HTMLElement {
    connectedCallback() {
      this.videos = Array.from(this.querySelectorAll('video'));
      if (!this.videos.length) return;

      this.addEventListener('click', this.onClick.bind(this));

      this.videos.forEach(
        function (video) {
          video.addEventListener('play', this.sync.bind(this, video));
          video.addEventListener('pause', this.sync.bind(this, video));
        }.bind(this)
      );

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting && !entry.target.paused) entry.target.pause();
            });
          },
          { threshold: 0.35 }
        );
        this.videos.forEach(
          function (v) {
            this.observer.observe(v);
          }.bind(this)
        );
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    onClick(e) {
      const play = e.target.closest('.cauce-ugc__play');
      if (play) {
        const video = play.parentElement.querySelector('video');
        if (!video) return;
        if (video.paused) {
          this.pausarResto(video);
          // play() devuelve una promesa que rechaza si el navegador lo bloquea
          // (por ejemplo Low Power Mode en iOS). Sin catch queda un unhandled
          // rejection en consola por cada bloqueo.
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        } else {
          video.pause();
        }
        return;
      }

      const sonido = e.target.closest('.cauce-ugc__sonido');
      if (sonido) {
        const video = sonido.parentElement.querySelector('video');
        if (!video) return;
        video.muted = !video.muted;
        sonido.setAttribute('aria-pressed', String(!video.muted));
        sonido.setAttribute(
          'aria-label',
          video.muted ? sonido.dataset.on || '' : sonido.dataset.off || ''
        );
        sonido.classList.toggle('cauce-ugc__sonido--activo', !video.muted);
      }
    }

    pausarResto(actual) {
      this.videos.forEach(function (v) {
        if (v !== actual && !v.paused) v.pause();
      });
    }

    sync(video) {
      const card = video.closest('.cauce-ugc__card');
      if (!card) return;
      const boton = card.querySelector('.cauce-ugc__play');
      card.classList.toggle('cauce-ugc__card--reproduciendo', !video.paused);
      if (boton) {
        boton.setAttribute(
          'aria-label',
          video.paused ? boton.dataset.play || '' : boton.dataset.pause || ''
        );
      }
    }
  }

  /* ----------------------------------------------------------------------
     <cauce-hero> — loop de video del hero.

     El <img> ya esta pintado y es el LCP; el video es atmosfera que se suma
     despues. Por eso nada arranca solo: el video sale con preload="none" y sin
     autoplay, y es este elemento el que decide si vale la pena bajarlo.

     No lo baja si el visitante pidio menos movimiento, si esta en ahorro de
     datos o si la conexion es 2g. Sin JS tampoco: queda el poster, que es la
     degradacion correcta porque el video no aporta informacion.

     El boton de pausa existe por WCAG 2.2.2 y aparece recien cuando el video
     efectivamente reproduce. Una pausa a mano gana siempre: el observer no
     vuelve a arrancar lo que el visitante freno.
     -------------------------------------------------------------------- */
  class CauceHero extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('.cauce-hero__video');
      if (!this.video) return;

      this.pausadoPorUsuario = false;
      this.boton = this.querySelector('.cauce-hero__pausa');
      if (this.boton) this.boton.addEventListener('click', this.toggle.bind(this));

      this.video.addEventListener('playing', this.sync.bind(this));
      this.video.addEventListener('pause', this.sync.bind(this));

      if (this.datosCaros()) return;

      // matchMedia con listener y no un chequeo unico: si alguien activa
      // "reducir movimiento" con la pagina abierta, el loop tiene que frenar.
      this.mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMq = function () {
        if (this.mq.matches) this.video.pause();
      }.bind(this);
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMq);
      if (this.mq.matches) return;

      if (!('IntersectionObserver' in window)) {
        this.arrancar();
        return;
      }

      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (entry) {
              if (entry.isIntersecting) this.arrancar();
              else if (!this.video.paused) this.video.pause();
            }.bind(this)
          );
        }.bind(this),
        { threshold: 0.2 }
      );
      this.observer.observe(this.video);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.mq && this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMq);
    }

    // navigator.connection no existe en Safari. Sin el dato se asume que se
    // puede: el loop ya es chico y va detras del poster en la cola de descarga.
    datosCaros() {
      const con = navigator.connection;
      if (!con) return false;
      return con.saveData === true || /(^|-)2g$/.test(con.effectiveType || '');
    }

    arrancar() {
      if (this.pausadoPorUsuario) return;
      if (this.video.preload === 'none') this.video.preload = 'auto';
      // play() devuelve una promesa que rechaza si el navegador lo bloquea (por
      // ejemplo Low Power Mode en iOS). Sin catch queda un unhandled rejection.
      const p = this.video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    toggle() {
      if (this.video.paused) {
        this.pausadoPorUsuario = false;
        this.arrancar();
      } else {
        this.pausadoPorUsuario = true;
        this.video.pause();
      }
    }

    sync() {
      const on = !this.video.paused;
      this.classList.toggle('cauce-hero__marco--reproduciendo', on);
      if (!this.boton) return;
      this.boton.hidden = false;
      this.boton.setAttribute(
        'aria-label',
        on ? this.boton.dataset.pause || '' : this.boton.dataset.play || ''
      );
    }
  }

  /* ----------------------------------------------------------------------
     <cauce-meses> — una pestana y una foto por mes
     (sections/cauce-meses.liquid, D-080).

     Elegir un mes es cambiar una clase y un aria-pressed: el tamano de la foto
     y el texto que se ve los resuelve CSS. Se elige con la pestana, tocando la
     foto o llegando con el foco a un enlace del texto.

     En telefono la fila es un carrusel de scroll-snap y el mes elegido es el
     que esta a la vista: lo decide la posicion del scroll, no el clic. La
     pestana solo desplaza la fila. Si ademas marcara el mes, el desplazamiento
     suave lo devolveria al de partida antes de llegar.

     Carrusel o grilla no se pregunta con un media query repetido aca: es
     carrusel si el CSS dejo la fila con scroll y el contenido no entra. Las
     dos cosas: en la grilla (overflow visible) una palabra larga puede
     desbordar una columna angosta, y eso no la vuelve carrusel.
     -------------------------------------------------------------------- */
  class CauceMeses extends HTMLElement {
    connectedCallback() {
      this.lista = this.querySelector('.cauce-meses__lista');
      this.items = Array.from(this.querySelectorAll('[data-cauce-mes]'));
      this.botones = Array.from(this.querySelectorAll('[data-cauce-mes-boton]'));
      if (!this.lista || this.items.length < 2) return;

      this.activo = 0;
      this.cuadro = 0;

      this.addEventListener('click', this.onElegir.bind(this));
      this.addEventListener('focusin', this.onElegir.bind(this));
      // Editor de temas: al elegir el bloque de un mes, se abre ese mes.
      this.addEventListener('shopify:block:select', this.onElegir.bind(this));
      this.lista.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    }

    disconnectedCallback() {
      if (this.cuadro) cancelAnimationFrame(this.cuadro);
    }

    esCarrusel() {
      return (
        getComputedStyle(this.lista).overflowX !== 'visible' &&
        this.lista.scrollWidth > this.lista.clientWidth + 1
      );
    }

    onElegir(e) {
      const boton = e.target.closest('[data-cauce-mes-boton]');
      // El foco sobre una pestana no elige: con Tab se pasa por las tres.
      if (boton && e.type === 'focusin') return;

      const i = boton
        ? this.botones.indexOf(boton)
        : this.items.indexOf(e.target.closest('[data-cauce-mes]'));
      if (i === -1) return;

      if (!this.esCarrusel()) {
        this.marcar(i);
        return;
      }

      const reducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.lista.scrollTo({
        left: this.items[i].offsetLeft - this.items[0].offsetLeft,
        behavior: reducido ? 'auto' : 'smooth',
      });
    }

    onScroll() {
      if (this.cuadro) return;
      this.cuadro = requestAnimationFrame(
        function () {
          this.cuadro = 0;
          if (this.esCarrusel()) this.marcar(this.aLaVista());
        }.bind(this)
      );
    }

    // El mes cuyo borde izquierdo quedo mas cerca del inicio de la fila. La
    // ultima tarjeta no llega a alinearse a la izquierda (la fila se termina
    // antes), asi que el final del scroll es siempre el ultimo mes.
    aLaVista() {
      const x = this.lista.scrollLeft;
      if (x >= this.lista.scrollWidth - this.lista.clientWidth - 2) return this.items.length - 1;

      const origen = this.items[0].offsetLeft;
      let cerca = 0;
      let minimo = Infinity;
      for (let i = 0; i < this.items.length; i++) {
        const d = Math.abs(this.items[i].offsetLeft - origen - x);
        if (d < minimo) {
          minimo = d;
          cerca = i;
        }
      }
      return cerca;
    }

    marcar(index) {
      if (index === this.activo) return;
      this.activo = index;
      for (let i = 0; i < this.items.length; i++) {
        const on = i === index;
        this.items[i].classList.toggle('cauce-meses__item--activo', on);
        if (this.botones[i]) this.botones[i].setAttribute('aria-pressed', String(on));
      }
    }
  }

  /* ----------------------------------------------------------------------
     Empujon de envio gratis del carrito: agrega una unidad por AJAX sin salir
     del drawer (snippets/cauce-carrito-nudge.liquid).

     No se porto el numen-cart-add.js de Numen (128 lineas de DOMParser y
     replaceWith): este tema ya expone el re-render correcto en sus propios
     custom elements, y usarlo es mas corto y no se desincroniza si Shrine
     cambia sus regiones.

       drawer -> <cart-drawer-items>.updateCart() pide las secciones cart-drawer
                 y cart-icon-bubble y llama a renderContents(data, true), que es
                 el mismo camino del update de cantidad. El "true" es "no
                 abras el drawer": ya esta abierto.
       /cart  -> <cart-items>.updateCart() re-renderiza
                 #main-cart-items .js-contents y #main-cart-footer .js-contents.

     El boton vive dentro de la region re-renderizada, asi que despues del add
     la card desaparece sola (el umbral ya esta cruzado) y no hay que resetear
     nada. Si el add falla, el mensaje va al contenedor de errores que el tema
     ya usa.
     -------------------------------------------------------------------- */
  function cauceCarritoRefrescar() {
    const objetivo =
      document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    if (objetivo && typeof objetivo.updateCart === 'function') {
      objetivo.updateCart();
      return true;
    }
    return false;
  }

  function cauceCarritoError(mensaje) {
    const caja =
      document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (caja) caja.textContent = mensaje || '';
  }

  function cauceCarritoSumar(boton) {
    const id = boton.dataset.cauceSumar;
    // La URL sale del Liquid del boton y NO de window.routes.cart_add_url. El
    // constructor de CartItems en main.js pisa esa global con "cart" cuando
    // pasaron 27 dias desde una fecha fija (2024-11-09): una ruta relativa que
    // desde una PDP resuelve a /products/cart, devuelve un 404 en HTML y el
    // r.json() cae en el catch. El add de Shrine no se entera porque usa
    // "/cart/add" fijo; cualquier JS propio que lea esa global se rompe.
    const url = boton.dataset.cauceUrl;
    if (!id || !url) return;

    const etiqueta = boton.querySelector('.cauce-carrito__nudge-label');
    const original = etiqueta ? etiqueta.textContent : '';
    if (etiqueta && boton.dataset.cauceSumando) etiqueta.textContent = boton.dataset.cauceSumando;
    boton.setAttribute('aria-busy', 'true');
    boton.disabled = true;
    cauceCarritoError('');

    const cuerpo = new FormData();
    cuerpo.append('id', id);
    cuerpo.append('quantity', 1);

    function fallar(mensaje) {
      if (etiqueta) etiqueta.textContent = original;
      boton.removeAttribute('aria-busy');
      boton.disabled = false;
      cauceCarritoError(mensaje);
    }

    fetch(url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: cuerpo,
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        // El endpoint de Shopify devuelve `status` solo cuando el add fallo
        // (sin stock, limite por pedido).
        if (data && data.status) {
          fallar(data.description || data.message || '');
          return;
        }
        if (!cauceCarritoRefrescar()) window.location.reload();
      })
      .catch(function () {
        fallar((window.cartStrings && window.cartStrings.error) || '');
      });
  }

  document.addEventListener('click', function (e) {
    const boton = e.target.closest('[data-cauce-sumar]');
    if (!boton) return;
    e.preventDefault();
    if (boton.disabled) return;
    cauceCarritoSumar(boton);
  });

  /* ----------------------------------------------------------------------
     El CTA dice lo que se esta por agregar (snippets/quantity-breaks.liquid).

     Cada tarjeta de escalon trae el texto ya armado en data-cauce-cta, con el
     money y la pluralizacion resueltos en el servidor. Aca no se arma texto ni
     se formatea plata: se copia el del escalon elegido al boton. Duplicar el
     formateo en JS es como se desincronizan los precios.

     No se toca el boton si esta agotado: ahi el texto es "Sin stock" y no una
     promesa de compra.

     El change va delegado en document y no un listener por radio: el tema
     re-renderiza el bloque al cambiar de variante y los listeners directos se
     perderian.
     -------------------------------------------------------------------- */
  function cauceCtaSync() {
    const elegido = document.querySelector(
      '.quantity-breaks-container input[name="quantity"]:checked'
    );
    if (!elegido) return;

    const tarjeta = document.querySelector('[data-cauce-cta][for="' + elegido.id + '"]');
    const texto = tarjeta && tarjeta.dataset.cauceCta;
    if (!texto) return;

    const boton =
      document.querySelector('.main-product-atc') ||
      document.querySelector('.product-form__submit');
    if (!boton || boton.dataset.unavailable === 'true') return;

    const etiqueta = boton.querySelector('.main-atc__label__text');
    if (etiqueta) etiqueta.textContent = texto;
  }

  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('.quantity-breaks-container input[name="quantity"]')) {
      cauceCtaSync();
    }
  });

  cauceCtaSync();

  /* ----------------------------------------------------------------------
     El boton flotante de WhatsApp (snippets/cauce-whatsapp.liquid) sube
     cuando aparece la barra fija de compra de la PDP, igual que el boton de
     volver arriba de Shrine.

     main.js esta ofuscado y no expone como muestra la barra, asi que no se
     engancha a su estado: se mide la barra misma. Lo que la barra tapa de la
     ventana es lo que el boton sube. Se remide cuando la barra termina su
     transicion (Shrine la esconde con un translateY de 0.15 s) y cuando cambia
     cualquier atributo suyo, que cubre un cambio sin transicion. Una barra
     con display:none mide 0 de alto y no tapa nada. Sin barra -el resto de
     las paginas- no hace nada.
     -------------------------------------------------------------------- */
  function cauceWhatsappSobreBarra() {
    const boton = document.querySelector('.cauce-wa--flotante');
    const barra = document.querySelector('.sticky-atc');
    if (!boton || !barra) return;

    function medir() {
      const r = barra.getBoundingClientRect();
      let tapa = 0;
      if (r.height > 0) {
        tapa = Math.min(r.height, Math.max(0, window.innerHeight - r.top));
      }
      boton.style.setProperty('--cauce-wa-offset', Math.round(tapa) + 'px');
    }

    barra.addEventListener('transitionend', function (e) {
      if (e.target === barra) medir();
    });
    new MutationObserver(medir).observe(barra, { attributes: true });
    window.addEventListener('resize', medir);
    medir();
  }

  cauceWhatsappSobreBarra();

  /* ----------------------------------------------------------------------
     Los datos que faltan se marcan solos (D-074).

     Una pagina que se arma antes de tener todos los datos lleva el hueco
     escrito en el texto: "[COMPLETAR: numero de certificado]". Vive en
     settings de texto plano y en richtext, donde no se puede poner una clase,
     asi que se envuelve aca con .cauce-pendiente para que se vea desde lejos.
     Solo recorre el DOM si el texto aparece: en una pagina sin huecos no hace
     nada. No toca <script>, <style> ni campos de formulario.
     -------------------------------------------------------------------- */
  function caucePendientes() {
    const raiz = document.getElementById('MainContent') || document.body;
    if (!raiz || raiz.textContent.indexOf('[COMPLETAR') === -1) return;

    const patron = /\[COMPLETAR[^\]]*\]/g;
    const walker = document.createTreeWalker(raiz, NodeFilter.SHOW_TEXT, {
      acceptNode: function (nodo) {
        const padre = nodo.parentElement;
        if (!padre || padre.closest('script, style, textarea, noscript, .cauce-pendiente')) {
          return NodeFilter.FILTER_REJECT;
        }
        return nodo.nodeValue.indexOf('[COMPLETAR') === -1
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_ACCEPT;
      }
    });

    const nodos = [];
    while (walker.nextNode()) nodos.push(walker.currentNode);

    nodos.forEach(function (nodo) {
      const texto = nodo.nodeValue;
      const trozo = document.createDocumentFragment();
      let desde = 0;
      let hallado;
      patron.lastIndex = 0;
      while ((hallado = patron.exec(texto))) {
        if (hallado.index > desde) {
          trozo.appendChild(document.createTextNode(texto.slice(desde, hallado.index)));
        }
        const marca = document.createElement('span');
        marca.className = 'cauce-pendiente';
        marca.textContent = hallado[0];
        trozo.appendChild(marca);
        desde = hallado.index + hallado[0].length;
      }
      if (desde < texto.length) trozo.appendChild(document.createTextNode(texto.slice(desde)));
      nodo.parentNode.replaceChild(trozo, nodo);
    });
  }

  caucePendientes();

  if (!customElements.get('cauce-tabs')) customElements.define('cauce-tabs', CauceTabs);
  if (!customElements.get('cauce-ugc')) customElements.define('cauce-ugc', CauceUgc);
  if (!customElements.get('cauce-hero')) customElements.define('cauce-hero', CauceHero);
  if (!customElements.get('cauce-meses')) customElements.define('cauce-meses', CauceMeses);
})();
